import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  Check,
  Download,
  Info,
  Loader2,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { MarkFiscalCompleteDialog } from "@/components/shared/MarkFiscalCompleteDialog";
import { StatusBadge } from "@/components/shared/StatusBadge";
import type { StatusBadgeVariant } from "@/components/shared/StatusBadge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { formatDisplayDate, parseLocalDateOnly } from "@/lib/dateFormat";
import {
  getTaxLabelKey,
  getAuthorityLabelKey,
  formatPeriodForDisplay,
  formatObligationAlertTitle,
  getStatusBadgeVariant,
} from "@/lib/fiscalAgendaDisplay";
import { dashboardKeys } from "@/lib/queryKeys";
import { useAuth } from "@/contexts/AuthContext";
import {
  dismissDashboardAlert,
  triggerPendingJobs,
} from "@/services/dashboardService";
import {
  getFiscalAgenda,
  markFiscalObligationCompleted,
  requestBoleto,
} from "@/services/fiscalService";
import type { FiscalAgendaObligation } from "@/services/fiscalService";
import { cn } from "@/lib/utils";
import {
  buildUnifiedAlerts,
  computeFiscalAlertContext,
  flattenAgenda,
  type UnifiedAlertItem,
} from "@/pages/dashboard/dashboardAlertsModel";
import type { DashboardAlert, DashboardMetrics } from "@fiscalo/shared-types";

interface DashboardRequiresUserActionProps {
  metrics: DashboardMetrics;
  alerts: DashboardAlert[];
  dismissedAlertIds: string[];
  recurringDraftsReadyCount: number;
}

function alertIcon(variant: "error" | "warning" | "info"): React.ReactNode {
  if (variant === "error")
    return (
      <AlertCircle className="h-4 w-4 shrink-0 text-[var(--fx-warn-text)] dark:text-[#E89978]" />
    );
  if (variant === "warning")
    return (
      <AlertTriangle className="h-4 w-4 shrink-0 text-[#8C5A1F] dark:text-[#E8C490]" />
    );
  return (
    <Info className="h-4 w-4 shrink-0 text-[var(--fx-accent-700)] dark:text-[var(--fx-accent-300)]" />
  );
}

function alertRowClass(variant: "error" | "warning" | "info"): string {
  const base =
    "rounded-md border-l-4 p-3 text-sm transition-colors duration-150";
  if (variant === "error")
    return cn(
      base,
      "border-l-[var(--fx-warn)] bg-[rgba(201,94,62,0.10)] dark:bg-[rgba(201,94,62,0.18)] dark:border-[rgba(201,94,62,0.45)]"
    );
  if (variant === "warning")
    return cn(
      base,
      "border-l-[#8C5A1F] bg-[rgba(232,196,144,0.18)] dark:bg-[rgba(232,196,144,0.10)] dark:border-[rgba(232,196,144,0.30)]"
    );
  return cn(
    base,
    "border-l-[var(--fx-accent-500)] bg-[rgba(45,97,73,0.08)] dark:bg-[rgba(111,165,133,0.10)] dark:border-[rgba(111,165,133,0.30)]"
  );
}

export function DashboardRequiresUserAction({
  metrics,
  alerts,
  dismissedAlertIds,
  recurringDraftsReadyCount,
}: DashboardRequiresUserActionProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAccountant = user?.role === "accountant";
  const [markCompleteObligation, setMarkCompleteObligation] =
    useState<FiscalAgendaObligation | null>(null);

  const requestBoletoMutation = useMutation({
    mutationFn: requestBoleto,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      queryClient.invalidateQueries({ queryKey: ["fiscal-agenda"] });
      toast.success(t("fiscalAgenda.requestDocumentSuccess", "Document requested from accountant."));
    },
    onError: () => {
      toast.error(t("fiscalAgenda.requestDocumentError", "Could not request document."));
    },
  });

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  const dismissMutation = useMutation({
    mutationFn: dismissDashboardAlert,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.metrics() });
    },
    onError: () => {
      toast.error(t("dashboard.activity.dismissError"));
    },
  });

  const triggerMutation = useMutation({
    mutationFn: triggerPendingJobs,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      const scheduled = res.scheduled ?? 0;
      const enqueued = res.enqueued ?? 0;
      if (scheduled > 0 || enqueued > 0) {
        toast.success(
          t("dashboard.activity.triggerPendingSuccessDetail", {
            scheduled,
            enqueued,
          })
        );
      } else {
        toast.success(t("dashboard.activity.triggerPendingNoJobs"));
      }
    },
    onError: () => {
      toast.error(t("dashboard.activity.triggerPendingError"));
    },
  });

  const markCompleteMutation = useMutation({
    mutationFn: (args: {
      obligationId: string;
      completedDate: string;
      notes?: string;
    }) =>
      markFiscalObligationCompleted(args.obligationId, {
        completedDate: args.completedDate,
        notes: args.notes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fiscal-agenda"] });
      queryClient.invalidateQueries({ queryKey: ["fiscal-health"] });
      queryClient.invalidateQueries({ queryKey: dashboardKeys.metrics() });
      toast.success(t("estadoFiscal.ivaMarkedAsPaid"));
      setMarkCompleteObligation(null);
    },
    onError: () => {
      toast.error(t("settings.fiscalProfile.error"));
    },
  });

  const handleDismissAlert = useCallback(
    (id: string) => {
      dismissMutation.mutate(id);
    },
    [dismissMutation]
  );

  const handleOpenFiscalAgenda = useCallback(() => {
    navigate("/agenda");
  }, [navigate]);

  const year = new Date().getFullYear();
  const { data: agendaGrouped } = useQuery({
    queryKey: ["fiscal-agenda", `${year}-01`, `${year}-12`],
    queryFn: () => getFiscalAgenda(`${year}-01`, `${year}-12`),
  });

  const fiscalContextDgi = useMemo(
    () => computeFiscalAlertContext(agendaGrouped, "DGI"),
    [agendaGrouped]
  );

  const bpsObligationsThisMonth = useMemo((): FiscalAgendaObligation[] => {
    if (!agendaGrouped) return [];
    const all = flattenAgenda(agendaGrouped);
    return all
      .filter((o) => {
        if (o.authority !== "BPS" || o.status === "completed") return false;
        const due = parseLocalDateOnly(o.dueDate);
        return (
          due.getFullYear() === currentYear && due.getMonth() === currentMonth
        );
      })
      .sort(
        (a, b) =>
          parseLocalDateOnly(a.dueDate).getTime() -
          parseLocalDateOnly(b.dueDate).getTime()
      );
  }, [agendaGrouped, currentYear, currentMonth]);

  const dgiObligationsThisMonth = useMemo((): FiscalAgendaObligation[] => {
    if (!agendaGrouped) return [];
    const all = flattenAgenda(agendaGrouped);
    return all
      .filter((o) => {
        if (o.authority !== "DGI" || o.status === "completed") return false;
        const due = parseLocalDateOnly(o.dueDate);
        return (
          due.getFullYear() === currentYear && due.getMonth() === currentMonth
        );
      })
      .sort(
        (a, b) =>
          parseLocalDateOnly(a.dueDate).getTime() -
          parseLocalDateOnly(b.dueDate).getTime()
      );
  }, [agendaGrouped, currentYear, currentMonth]);

  const allAlerts = useMemo(
    () => buildUnifiedAlerts(metrics, alerts, fiscalContextDgi),
    [metrics, alerts, fiscalContextDgi]
  );

  const visibleAlerts = useMemo(
    () => allAlerts.filter((item) => !dismissedAlertIds.includes(item.id)),
    [allAlerts, dismissedAlertIds]
  );

  const formatAlertMessage = useCallback(
    (item: UnifiedAlertItem): string => {
      if (item.obligationForDetail && item.detailMessageKey) {
        const obligation = formatObligationAlertTitle(
          item.obligationForDetail,
          t,
          i18n.language
        );
        return t(item.detailMessageKey, { obligation });
      }
      if (item.messageKey) {
        if (item.messageOptions && "name" in item.messageOptions) {
          const opts = {
            ...item.messageOptions,
            name: t(item.messageOptions.name as string),
          };
          return t(item.messageKey, opts);
        }
        return t(item.messageKey, item.messageOptions);
      }
      return item.message ?? "";
    },
    [t, i18n.language]
  );

  const hasContent =
    recurringDraftsReadyCount > 0 ||
    bpsObligationsThisMonth.length > 0 ||
    dgiObligationsThisMonth.length > 0 ||
    visibleAlerts.length > 0;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold text-foreground">
            {t("dashboard.requiresAction.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!hasContent ? (
            <p className="text-sm text-muted-foreground">
              {t("dashboard.requiresAction.empty")}
            </p>
          ) : (
            <ul className="space-y-2">
              {recurringDraftsReadyCount > 0 ? (
                <li>
                  <div
                    className={cn(
                      "flex flex-col gap-2 rounded-md border-l-4 p-3 text-sm transition-colors duration-150",
                      "border-l-[var(--fx-ink-900)] bg-[rgba(22,21,20,0.05)] dark:border-l-[var(--fx-paper)] dark:bg-[rgba(244,239,230,0.06)]"
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <Info className="h-4 w-4 shrink-0 text-primary" />
                      <span className="min-w-0 flex-1 font-semibold text-foreground">
                        {t("dashboard.requiresAction.recurringDrafts", {
                          count: recurringDraftsReadyCount,
                        })}
                      </span>
                    </div>
                    <Button variant="outline" size="sm" asChild className="w-fit">
                      <Link to="/invoices/recurring">
                        {t("dashboard.requiresAction.reviewRecurring")}
                      </Link>
                    </Button>
                  </div>
                </li>
              ) : null}
              {bpsObligationsThisMonth.map((ob) => {
                const isPending =
                  markCompleteMutation.isPending &&
                  markCompleteObligation?.id === ob.id;
                const isDueToday = ob.status === "due_today";
                const isOverdue = ob.status === "overdue";
                return (
                  <li key={ob.id}>
                    <div
                      className={cn(
                        "flex flex-col gap-2 rounded-md border-l-4 p-3 text-sm transition-colors duration-150",
                        isOverdue
                          ? "border-l-[var(--fx-warn)] bg-[rgba(201,94,62,0.10)] dark:bg-[rgba(201,94,62,0.18)] dark:border-[rgba(201,94,62,0.45)]"
                          : "border-l-[#8C5A1F] bg-[rgba(232,196,144,0.18)] dark:bg-[rgba(232,196,144,0.10)] dark:border-[rgba(232,196,144,0.30)]"
                      )}
                    >
                      <div className="flex items-start gap-2">
                        <Calendar className="h-4 w-4 shrink-0 text-[#8C5A1F] dark:text-[#E8C490]" />
                        <div className="min-w-0 flex-1">
                          <span className="font-semibold">
                            {t(getTaxLabelKey(ob.taxType))} (
                            {t(getAuthorityLabelKey(ob.authority))})
                          </span>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {formatPeriodForDisplay(ob.period, i18n.language)} ·{" "}
                            {t("estadoFiscal.dueDateLabel")}:{" "}
                            {formatDisplayDate(ob.dueDate, {
                              useCalendarDate: true,
                              locale: i18n.language,
                            })}
                          </p>
                          <StatusBadge
                            variant={getStatusBadgeVariant(ob.status)}
                            className={cn(
                              "mt-1 inline-flex text-xs font-medium",
                              (isDueToday || isOverdue) && "font-semibold"
                            )}
                          >
                            {t(`fiscalAgenda.status.${ob.status}`)}
                          </StatusBadge>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setMarkCompleteObligation(ob)}
                          disabled={isPending}
                          className="gap-1.5 transition-colors duration-150 hover:bg-muted hover:text-foreground"
                        >
                          {isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4 text-[var(--fx-accent-700)]" />
                          )}
                          {t("fiscalAgenda.markCompleted")}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleOpenFiscalAgenda}
                          className="text-muted-foreground transition-colors duration-150 hover:text-foreground"
                          asChild
                        >
                          <Link to="/agenda">
                            {t("estadoFiscal.viewFiscalAgenda")}
                          </Link>
                        </Button>
                        {ob.boletoFileUrl && ob.boletoUploadedAt ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              window.open(ob.boletoFileUrl ?? "", "_blank")
                            }
                            className="gap-1.5 transition-colors duration-150 hover:bg-muted hover:text-foreground"
                          >
                            <Download className="h-4 w-4" />
                            {t("fiscalAgenda.downloadDocument")}
                          </Button>
                        ) : !isAccountant && !ob.boletoRequestedAt ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => requestBoletoMutation.mutate(ob.id)}
                            disabled={
                              requestBoletoMutation.isPending &&
                              requestBoletoMutation.variables === ob.id
                            }
                            className="gap-1.5 transition-colors duration-150 hover:bg-muted hover:text-foreground"
                          >
                            {requestBoletoMutation.isPending &&
                            requestBoletoMutation.variables === ob.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : null}
                            {t("fiscalAgenda.requestDocument")}
                          </Button>
                        ) : ob.boletoRequestedAt ? (
                          <Badge variant="secondary" className="inline-flex w-fit text-xs font-medium">
                            {t("fiscalAgenda.requested")}
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                  </li>
                );
              })}
              {dgiObligationsThisMonth.map((ob) => {
                const isPending =
                  markCompleteMutation.isPending &&
                  markCompleteObligation?.id === ob.id;
                const isDueToday = ob.status === "due_today";
                const isOverdue = ob.status === "overdue";
                return (
                  <li key={ob.id}>
                    <div
                      className={cn(
                        "flex flex-col gap-2 rounded-md border-l-4 p-3 text-sm transition-colors duration-150",
                        isOverdue
                          ? "border-l-[var(--fx-warn)] bg-[rgba(201,94,62,0.10)] dark:bg-[rgba(201,94,62,0.18)] dark:border-[rgba(201,94,62,0.45)]"
                          : "border-l-[var(--fx-accent-500)] bg-[rgba(45,97,73,0.08)] dark:bg-[rgba(111,165,133,0.10)] dark:border-[rgba(111,165,133,0.30)]"
                      )}
                    >
                      <div className="flex items-start gap-2">
                        <Calendar className="h-4 w-4 shrink-0 text-[var(--fx-accent-700)] dark:text-[var(--fx-accent-300)]" />
                        <div className="min-w-0 flex-1">
                          <span className="font-semibold">
                            {t(getTaxLabelKey(ob.taxType))} (
                            {t(getAuthorityLabelKey(ob.authority))})
                          </span>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {formatPeriodForDisplay(ob.period, i18n.language)} ·{" "}
                            {t("estadoFiscal.dueDateLabel")}:{" "}
                            {formatDisplayDate(ob.dueDate, {
                              useCalendarDate: true,
                              locale: i18n.language,
                            })}
                          </p>
                          <StatusBadge
                            variant={getStatusBadgeVariant(ob.status)}
                            className={cn(
                              "mt-1 inline-flex text-xs font-medium",
                              (isDueToday || isOverdue) && "font-semibold"
                            )}
                          >
                            {t(`fiscalAgenda.status.${ob.status}`)}
                          </StatusBadge>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setMarkCompleteObligation(ob)}
                          disabled={isPending}
                          className="gap-1.5 transition-colors duration-150 hover:bg-muted hover:text-foreground"
                        >
                          {isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4 text-[var(--fx-accent-700)]" />
                          )}
                          {t("fiscalAgenda.markCompleted")}
                        </Button>
                        {ob.taxType === "IVA" && (
                          <Button
                            type="button"
                            size="sm"
                            onClick={handleOpenFiscalAgenda}
                            className="gap-1.5 transition-colors duration-150"
                            asChild
                          >
                            <Link to="/estado-fiscal">
                              {t("dashboard.dgiObligation.generateBoleto", "Generar boleto de pago")}
                            </Link>
                          </Button>
                        )}
                        {ob.taxType !== "IVA" && !isAccountant && !ob.boletoRequestedAt && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => requestBoletoMutation.mutate(ob.id)}
                            disabled={requestBoletoMutation.isPending && requestBoletoMutation.variables === ob.id}
                            className="gap-1.5 transition-colors duration-150 hover:bg-muted hover:text-foreground"
                          >
                            {requestBoletoMutation.isPending && requestBoletoMutation.variables === ob.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : null}
                            {t("fiscalAgenda.requestDocument")}
                          </Button>
                        )}
                        {ob.taxType !== "IVA" && ob.boletoRequestedAt && (
                          <Badge variant="secondary" className="inline-flex w-fit text-xs font-medium">
                            {t("fiscalAgenda.requested")}
                          </Badge>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-muted-foreground transition-colors duration-150 hover:text-foreground"
                          asChild
                        >
                          <Link to="/agenda">
                            {t("estadoFiscal.viewFiscalAgenda")}
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </li>
                );
              })}
              {visibleAlerts.map((item) => (
                <li key={item.id}>
                  <div
                    className={cn(
                      "flex flex-col gap-2",
                      alertRowClass(item.variant)
                    )}
                  >
                    <div className="flex items-start gap-2">
                      {alertIcon(item.variant)}
                      <span className="min-w-0 flex-1 font-semibold">
                        {formatAlertMessage(item)}
                      </span>
                      {item.dismissible ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 shrink-0 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                          onClick={() => handleDismissAlert(item.id)}
                          aria-label={t("dashboard.activity.dismissAlert")}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {item.id === "pending-retries" ? (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-fit"
                          disabled={triggerMutation.isPending}
                          onClick={() => triggerMutation.mutate()}
                        >
                          {triggerMutation.isPending ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : null}
                          {t("dashboard.activity.triggerPendingNow")}
                        </Button>
                      ) : null}
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="w-fit"
                      >
                        <Link to={item.actionUrl}>{t(item.ctaKey)}</Link>
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <MarkFiscalCompleteDialog
        obligation={markCompleteObligation}
        onClose={() => setMarkCompleteObligation(null)}
        onConfirm={(completedDate, notes) => {
          if (!markCompleteObligation) return;
          markCompleteMutation.mutate({
            obligationId: markCompleteObligation.id,
            completedDate,
            notes: notes || undefined,
          });
        }}
        isSubmitting={markCompleteMutation.isPending}
      />
    </>
  );
}
